import React from 'react';
import { Image as RNImage } from 'expo-image';

import { Box, Text, useTheme } from 'app/shopify/theme';

export type AvatarSize = 16 | 20 | 24 | 32 | 40 | 48 | 56;
export type AvatarStyle = 'rounded' | 'squared';
export type AvatarMedia = 'image' | 'text';
export type AvatarTint =
  | 'brand-alt'
  | 'brand-secondary-1'
  | 'brand-secondary-2'
  | 'brand-secondary-3'
  | 'brand-secondary-4'
  | 'brand-secondary-5';

export interface AvatarProps {
  size?: AvatarSize;
  style?: AvatarStyle;
  media?: AvatarMedia;
  tint?: AvatarTint;
  initials?: string;
  imageUri?: string;
}

const AVATAR_TEMP_ASSET = 'https://www.figma.com/api/mcp/asset/dce6f959-c6aa-4c2e-8c92-cb20291677aa';

const TINT_TO_BACKGROUND = {
  'brand-alt': 'surface.tertiary',
  'brand-secondary-1': 'surface.brand.secondary-1.default',
  'brand-secondary-2': 'surface.brand.secondary-2.default',
  'brand-secondary-3': 'surface.brand.secondary-3.default',
  'brand-secondary-4': 'surface.brand.secondary-4.default',
  'brand-secondary-5': 'surface.brand.secondary-5.default',
} as const;

function getBorderRadiusKey(shape: AvatarStyle, size: AvatarSize) {
  if (shape === 'rounded') return 'radius.full';
  if (size <= 24) return 'radius.xs';
  if (size <= 40) return 'radius.sm';
  return 'radius.md';
}

function getInitialsTypography(size: AvatarSize, shape: AvatarStyle) {
  if (size === 56) return { variant: 'headerSmall' as const, useBoldBody: false };
  if (size === 48) return { variant: 'headerXSmall' as const, useBoldBody: false };
  if (size === 40 && shape === 'squared') return { variant: 'headerXSmall' as const, useBoldBody: false };

  if (size === 16) return { variant: 'bodyCaption' as const, useBoldBody: true };
  if (size === 20) return { variant: 'bodyCaption' as const, useBoldBody: true };
  if (size === 24) return { variant: 'bodyXSmall' as const, useBoldBody: true };
  if (size === 32) return { variant: 'bodySmall' as const, useBoldBody: true };
  return { variant: 'bodyBase' as const, useBoldBody: true };
}

export function Avatar({
  size = 16,
  style = 'rounded',
  media = 'image',
  tint = 'brand-alt',
  initials = 'HL',
  imageUri = AVATAR_TEMP_ASSET,
}: AvatarProps) {
  const theme = useTheme();
  const borderRadiusKey = getBorderRadiusKey(style, size);
  const borderRadius = theme.borderRadii[borderRadiusKey];

  if (media === 'image') {
    return (
      <Box width={size} height={size} borderRadius={borderRadiusKey} style={{ overflow: 'hidden' }}>
        <RNImage
          source={{ uri: imageUri }}
          style={{ width: '100%', height: '100%', borderRadius }}
          contentFit="cover"
        />
      </Box>
    );
  }

  const typography = getInitialsTypography(size, style);
  const bodyBoldFont = theme.tokens.typography['typography.font-family.body-bold'];

  return (
    <Box
      width={size}
      height={size}
      borderRadius={borderRadiusKey}
      backgroundColor={TINT_TO_BACKGROUND[tint]}
      alignItems="center"
      justifyContent="center"
    >
      <Text
        variant={typography.variant}
        color="black"
        style={typography.useBoldBody && bodyBoldFont ? { fontFamily: bodyBoldFont } : undefined}
      >
        {initials}
      </Text>
    </Box>
  );
}
